import { stockApi } from '@/api/stockApi'

function todayYmd() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 默认 as-of = 本地今日（不再写死试点日） */
const DEFAULT_TRADE_DATE = todayYmd()

function clone(obj) {
  return JSON.parse(JSON.stringify(obj))
}

export function emptyErrorSnapshot(message, tradeDate) {
  const td = tradeDate || DEFAULT_TRADE_DATE
  return {
    meta: {
      snapshotId: 'LCC-V2-REAL-ERROR',
      mode: 'REAL',
      banner: 'ERROR｜正式数据不可用，已禁止回落 Demo。' + (message ? ' ' + message : ''),
      asofDate: td,
      asofLabel: '收盘',
      asofTime: '',
      researchMaturity: 'RESEARCH',
      dataStatus: 'ERROR',
      marketMaxBoard: { value: null, unit: 'B', quality: 'ERROR' },
      effectiveTurnoverHeight: { value: null, unit: 'B', quality: 'NOT_IMPLEMENTED' },
      observationTotal: null,
      observationTotalQuality: 'ERROR',
      changePctScope: 'CLOSE',
      returnWindow: { startDate: null, endDate: td, basis: 'QFQ_RESEARCH_STOCK_DAILY_PRICES' }
    },
    abcdCatalog: {
      A: { code: 'A', title: '高度统治', explain: 'NOT_IMPLEMENTED' },
      B: { code: 'B', title: '价格发现', explain: 'NOT_IMPLEMENTED' },
      C: { code: 'C', title: '压力→重新主动', explain: 'NOT_IMPLEMENTED' },
      D: { code: 'D', title: '一字空间', explain: 'NOT_IMPLEMENTED' }
    },
    leaders: [],
    catchupByLeaderId: {},
    catchupStatus: 'NOT_IMPLEMENTED',
    source: 'ERROR',
    connected: false,
    demoMode: false
  }
}

export function emptyCandidateState(tradeDate) {
  return {
    ok: false,
    meta: {
      tradeDate: tradeDate || DEFAULT_TRADE_DATE,
      dataStatus: 'ERROR',
      candidateWindow: '20D'
    },
    candidatePool: { window: '20D', count: 0, items: [] },
    highAttentionPool: { count: 0, items: [] },
    userPinned: { count: null, items: [] },
    ownerCases: [],
    abcdEvidenceLayer: { status: 'NOT_IMPLEMENTED' },
    nfiAudit: { auditN: 0, pass: 0, fail: 0, status: 'UNKNOWN' }
  }
}

export function emptyMonitoringState(tradeDate) {
  return {
    ok: false,
    date: tradeDate || DEFAULT_TRADE_DATE,
    meta: {
      tradeDate: tradeDate || DEFAULT_TRADE_DATE,
      dataStatus: 'ERROR',
      candidateWindow: '20D'
    },
    candidatePool: { window: '20D', count: 0, items: [] },
    highAttentionPool: { count: 0, items: [] },
    activeMonitoring: { count: 0, items: [] },
    removedMonitoring: { count: 0, items: [] },
    removedReview: { count: 0, items: [] },
    ownerCases: [],
    nfiAudit: { auditN: 0, pass: 0, fail: 0, status: 'UNKNOWN' }
  }
}

/** Map candidate / monitoring API item → card shape for LeaderColumn. */
export function candidateItemToCard(item) {
  if (!item) return null
  const state = item.state || item.lifecycleState || item.attentionLevel || 'ACTIVE_MONITORING'
  return {
    leaderId: item.episodeId || item.code,
    code: item.code,
    name: item.name,
    displayOrder: item.displayOrder || 0,
    closePrice: item.closePrice != null ? item.closePrice : null,
    changePct: item.changePct != null ? item.changePct : null,
    changePctQuality: item.changePctQuality || (item.changePct != null ? 'VALID' : 'MISSING'),
    changePctScope: item.changePctScope || 'CLOSE',
    priceBasis: item.priceBasis || null,
    boardStage: item.currentBoard,
    boardStageLabel: item.boardTierLabel || item.boardStageLabel || '—',
    nDayMBoard: item.boardTierLabel || item.nDayMBoard || item.boardStageLabel || '—',
    boardTierLabel: item.boardTierLabel || item.boardStageLabel || null,
    returnRank: item.returnRank || null,
    candidateStatus: item.candidateStatus || 'FOUR_BOARD_CANDIDATE',
    attentionLevel: state,
    attentionReason: item.stateReason || item.attentionReason || ['FOUR_BOARD'],
    filterC: item.filterC || null,
    fourBoardDate: item.fourBoardDate,
    entryDate: item.entryDate,
    entryReason: item.entryReason || 'HAS_4B_LAST_20D',
    manualAdd: !!item.manualAdd,
    episodeId: item.episodeId,
    daysInMonitor: item.daysInMonitor,
    lifecycleState: state,
    membership: item.membership || (state === 'REMOVED_FROM_MONITORING' ? 'REMOVED' : 'ACTIVE'),
    exitDate: item.exitDate || null,
    exitReason: item.exitReason || null,
    exitSignalDate: item.exitSignalDate || null,
    exitInfo: item.exitInfo || null,
    maxBoardAsOf: item.maxBoardAsOf != null ? item.maxBoardAsOf : null,
    stateEvidence: item.stateEvidence || null,
    evidenceHints: item.evidenceHints || (item.stateEvidence && item.stateEvidence.displayHints) || [],
    oneWordEvidence: item.oneWordEvidence,
    priceEvidence: item.priceEvidence,
    competitionEvidence: item.competitionEvidence,
    nodeEvidence: item.nodeEvidence,
    volumeEvidence: item.volumeEvidence,
    evidenceState: item.evidenceState || 'RESEARCH',
    dataQuality: item.dataQuality,
    identity: { quality: 'NOT_IMPLEMENTED', label: 'NOT_IMPLEMENTED' },
    competition: { quality: 'NOT_IMPLEMENTED', label: 'NOT_IMPLEMENTED' },
    lifecycle: { quality: 'RESEARCH', label: state, value: state },
    nextWatch: { text: 'NOT_IMPLEMENTED', quality: 'NOT_IMPLEMENTED' },
    evidence: { hits: [], quality: 'NOT_IMPLEMENTED' },
    systemPrimaryAttr: null,
    attrSummary: [],
    allAttrs: [],
    returns: item.returns || { d10: null, d15: null, d20: null, quality: 'MISSING', priceBasis: 'QFQ' },
    lifecycleMarks: [],
    lifecycleActive: state,
    cardKind: item.cardKind || 'MONITORING'
  }
}

export async function loadLeaderCommandMonitoring(tradeDate, manualEpisodes) {
  const td = tradeDate || DEFAULT_TRADE_DATE
  const payload = await stockApi.getLeaderCommandMonitoring({
    tradeDate: td,
    decisionTime: 'EOD',
    mode: 'REAL',
    includeNfiAudit: false,
    manualEpisodes: Array.isArray(manualEpisodes) ? manualEpisodes : []
  })
  if (!payload || payload.ok === false || payload.demoMode === true) {
    const err = new Error((payload && (payload.error || payload.message)) || 'REAL monitoring unavailable')
    err.payload = payload
    throw err
  }
  return clone(payload)
}

export async function loadLeaderCommandSnapshot(tradeDate) {
  const td = tradeDate || DEFAULT_TRADE_DATE
  const payload = await stockApi.getLeaderCommandSnapshot({
    tradeDate: td,
    scopeId: 'LIMIT_UP_LIST',
    decisionTime: 'EOD',
    mode: 'REAL'
  })
  if (!payload || payload.ok === false || payload.demoMode === true) {
    const err = new Error((payload && (payload.error || payload.message)) || 'REAL snapshot unavailable')
    err.payload = payload
    throw err
  }
  const snap = clone(payload)
  return {
    ...snap,
    leaders: snap.leaders || snap.leaderCards || [],
    catchupByLeaderId: {},
    source: 'REAL',
    connected: true,
    demoMode: false
  }
}

export async function loadLeaderCommandCandidate(tradeDate) {
  const td = tradeDate || DEFAULT_TRADE_DATE
  const payload = await stockApi.getLeaderCommandCandidate({
    tradeDate: td,
    decisionTime: 'EOD',
    mode: 'REAL',
    includeNfiAudit: false
  })
  if (!payload || payload.ok === false || payload.demoMode === true) {
    const err = new Error((payload && (payload.error || payload.message)) || 'REAL candidate unavailable')
    err.payload = payload
    throw err
  }
  return clone(payload)
}

/** Rank d10/d15/d20 within a card list (desc). Ties share best rank. */
export function attachReturnRanks(cards) {
  const list = (cards || []).slice()
  const keys = ['d10', 'd15', 'd20']
  const ranks = {}
  keys.forEach((key) => {
    const scored = list
      .map((c, idx) => {
        const v = c.returns && c.returns[key]
        const n = v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v)
        return { idx, n }
      })
      .filter((x) => x.n != null)
      .sort((a, b) => b.n - a.n)
    const rankByIdx = {}
    let i = 0
    while (i < scored.length) {
      let j = i
      while (j + 1 < scored.length && scored[j + 1].n === scored[i].n) j += 1
      const rank = i + 1
      for (let k = i; k <= j; k += 1) rankByIdx[scored[k].idx] = rank
      i = j + 1
    }
    ranks[key] = rankByIdx
  })
  return list.map((c, idx) =>
    Object.assign({}, c, {
      returnRank: {
        d10: ranks.d10[idx] != null ? ranks.d10[idx] : null,
        d15: ranks.d15[idx] != null ? ranks.d15[idx] : null,
        d20: ranks.d20[idx] != null ? ranks.d20[idx] : null
      }
    })
  )
}

export function uniqueByCode(rows) {
  const seen = new Set()
  const out = []
  ;(rows || []).forEach((row) => {
    const key = String(row.code || '')
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push(row)
  })
  return out
}

export function filterLeadersByTab(leaders, tab) {
  const list = leaders || []
  if (!tab || tab === 'priority') return list.slice()
  return list.filter((l) => ((l.evidence && l.evidence.hits) || []).includes(tab))
}

export function searchLeaders(leaders, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return leaders.slice()
  return (leaders || []).filter((l) => {
    const bag = [
      l.name,
      l.code,
      l.fourBoardDate,
      l.attentionLevel,
      l.candidateStatus,
      l.systemPrimaryAttr && l.systemPrimaryAttr.label,
      ...((l.allAttrs || []).map((a) => a.label)),
      ...((l.attrSummary || []).map((a) => a.label))
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return bag.indexOf(q) >= 0
  })
}

function cmpNumDesc(av, bv, a, b) {
  if (av === null && bv === null) return (a.displayOrder || 0) - (b.displayOrder || 0)
  if (av === null) return 1
  if (bv === null) return -1
  if (bv !== av) return bv - av
  return (a.displayOrder || 0) - (b.displayOrder || 0)
}

/** 20日涨幅 Top3 占 1/2/3，其余按 10日涨幅降序接在第4位起 */
function sortD20Top3ThenD10(leaders) {
  const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
  const d10 = (x) => num(x && x.returns && x.returns.d10)
  const d20 = (x) => num(x && x.returns && x.returns.d20)
  const list = (leaders || []).slice()
  const byD20 = list
    .map((item, idx) => ({ item, idx, v: d20(item) }))
    .filter((x) => x.v != null)
    .sort((a, b) => {
      if (b.v !== a.v) return b.v - a.v
      return a.idx - b.idx
    })
  const top3 = byD20.slice(0, 3).map((x) => x.item)
  const topIds = new Set(top3.map((x) => x.leaderId || x.code))
  const rest = list
    .filter((x) => !topIds.has(x.leaderId || x.code))
    .sort((a, b) => cmpNumDesc(d10(a), d10(b), a, b))
  return top3.concat(rest)
}

function isLimitUpCard(x) {
  const se = x && x.stateEvidence
  const f = se && se.structureFacts && se.structureFacts.LIMIT_UP_TODAY
  if (f && (f.value === 'YES' || f.value === true)) return true
  if (x && x.currentBoard != null && Number(x.currentBoard) > 0) return true
  const lab = x && (x.boardTierLabel || x.boardStageLabel || x.nDayMBoard)
  if (lab && /^\d+天\d+板$/.test(String(lab))) return true
  return false
}

/**
 * 今日涨停优先 → 今日涨幅≥5% → 其余；
 * 各组内仍：20日 Top3 占 1/2/3，其余按 10日降序。
 */
function sortD20Top3ThenD10WithBoost(leaders) {
  const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
  const list = (leaders || []).slice()
  const lu = []
  const ge5 = []
  const rest = []
  list.forEach((x) => {
    if (isLimitUpCard(x)) lu.push(x)
    else if (num(x.changePct) != null && num(x.changePct) >= 5) ge5.push(x)
    else rest.push(x)
  })
  return sortD20Top3ThenD10(lu)
    .concat(sortD20Top3ThenD10(ge5))
    .concat(sortD20Top3ThenD10(rest))
}

export function sortLeaders(leaders, sortKey) {
  const list = (leaders || []).slice()
  const key = sortKey || 'd20Top3ThenD10'
  const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
  const d10 = (x) => num(x && x.returns && x.returns.d10)
  if (key === 'd20Top3ThenD10') {
    return sortD20Top3ThenD10WithBoost(list)
  }
  list.sort((a, b) => {
    if (key === 'returnD10') {
      return cmpNumDesc(d10(a), d10(b), a, b)
    }
    if (key === 'apiOrder' || key === 'displayOrder') {
      return (a.displayOrder || 0) - (b.displayOrder || 0)
    }
    if (key === 'changePct') {
      const av = num(a.changePct)
      const bv = num(b.changePct)
      return cmpNumDesc(av, bv, a, b)
    }
    if (key === 'boardStage') {
      const av = num(a.boardStage)
      const bv = num(b.boardStage)
      return cmpNumDesc(av, bv, a, b)
    }
    return (a.displayOrder || 0) - (b.displayOrder || 0)
  })
  return list
}

export function getCatchupForLeader() {
  return []
}

export function formatSignedPct(value, digits) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  const d = digits === undefined ? 2 : digits
  const abs = Math.abs(n).toFixed(d)
  if (n > 0) return '+' + abs + '%'
  if (n < 0) return '-' + abs + '%'
  return abs + '%'
}

/** Card/status Chinese label; keep technical enums in API / drawer details. */
export function pendingLabel(raw) {
  const s = String(raw || '').trim()
  const u = s.toUpperCase()
  if (!s || u === 'NOT_IMPLEMENTED' || u === 'NONE' || u === 'UNKNOWN' || s === '待接入') return '待接入'
  return s
}

export function qualityLabel(quality) {
  const q = String(quality || '').toUpperCase()
  if (q === 'NOT_IMPLEMENTED') return '待接入'
  if (
    q === 'MISSING' ||
    q === 'UNKNOWN' ||
    q === 'PARTIAL' ||
    q === 'STALE' ||
    q === 'ERROR' ||
    q === 'VALID' ||
    q === 'NOT_ELIGIBLE' ||
    q === 'REAL'
  ) {
    return q
  }
  if (q === 'DEMO') return 'ERROR'
  return quality || ''
}

export function displayStat(block, fallbackQuality) {
  if (block == null) {
    const fb = String(fallbackQuality || '—')
    return fb.toUpperCase() === 'NOT_IMPLEMENTED' ? '待接入' : fb
  }
  if (typeof block === 'number') return String(block)
  const q = String(block.quality || block.status || '').toUpperCase()
  if (q === 'NOT_IMPLEMENTED') return '待接入'
  if (q === 'MISSING' || q === 'UNKNOWN' || q === 'ERROR' || q === 'STALE') {
    return q
  }
  if (block.value === null || block.value === undefined || block.value === '') {
    return q === 'NOT_IMPLEMENTED' ? '待接入' : q || '—'
  }
  return String(block.value) + (block.unit || '')
}

export function attentionLabel(level) {
  const u = String(level || '').toUpperCase()
  if (u === 'HIGH_ATTENTION_REVIEW') return '重点观察'
  if (u === 'NORMAL_TRACKING_REVIEW') return '普通跟踪'
  if (u === 'REMOVE_REVIEW') return '降低关注'
  return pendingLabel(level)
}

export { DEFAULT_TRADE_DATE, DEFAULT_TRADE_DATE as PILOT_TRADE_DATE }
